import {WriteButton} from '../common/buttons/WriteActions';
import {PackageTable} from "./packagings/PackageTable";
import {Alert, Box, InputAdornment, Stack, TextField} from "@mui/material";
import React, {useState} from "react";
import {packageAPI} from "../../services/PackageServices";
import {getApiErrorMessage} from '../../common/apiErrors';
import AddIcon from "@mui/icons-material/Add";
import {PackageGuidDto} from "../../models/Packages/IPackage";
import {usePageLimit} from "../../hooks/usePageLimit";
import {CreatePackageDialog} from "./packagings/dialogs/CreatePackageDialog";
import IconButton from "@mui/material/IconButton";
import {Clear} from "@mui/icons-material";
import useDebounce from "../../hooks/useDebounce";
import {AddPackagePurchaseDialog} from './packagings/dialogs/AddPackagePurchaseDialog';
import {EditPackageDialog} from './packagings/dialogs/EditPackageDialog';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {PageHeader} from '../common/layout/PageHeader';
import {Surface} from '../common/layout/Surface';
import {ContentSkeleton} from '../common/loading/ContentSkeleton';

type SearchPropsType = {
    page: number;
    pageSize: number;
    searchPhrase: string;
    descending: boolean;
    orderByName: boolean;
};

function Packagings() {
    const {t} = useAppTranslation();
    const [page, setPage] = useState(0);
    const [limit, setLimit] = usePageLimit('packages');
    const [searchPhrase, setSearchPhrase] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [descending, setDescending] = useState(false);
    const orderByName = true;
    const debouncedSearchPhrase = useDebounce(searchPhrase, 600);

    const {
        data: response,
        error,
        isLoading,
        isSuccess,
    } = packageAPI.useGetPackagesByPagesQuery({
        page,
        pageSize: limit,
        searchPhrase: debouncedSearchPhrase.trim().length >= 3
            ? debouncedSearchPhrase.trim()
            : "",
        descending,
        orderByName,
    } as SearchPropsType);

    const handlePageChange = (newPage: number) => setPage(newPage);

    const handlePageSizeChange = (newSize: number) => {
        setLimit(newSize);
        setPage(0);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };
    const [selectedPackage, setSelectedPackage] = useState<PackageGuidDto | undefined>(undefined);
    const [purchaseOpen, setPurchaseOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [expandedPackageId, setExpandedPackageId] = useState<string | null>(null);
    const openPurchase = (pack: PackageGuidDto) => { setSelectedPackage(pack); setPurchaseOpen(true); };
    const openEdit = (pack: PackageGuidDto) => { setSelectedPackage(pack); setEditOpen(true); };
    const handleCloseModal = () => setIsModalOpen(false);

    return (
        <Box sx={{width: '100%'}}>
            <PageHeader title={t('navigation:nav_packaging')} action={<WriteButton variant="contained" startIcon={<AddIcon/>} onClick={handleOpenModal}>{t('items:create_package')}</WriteButton>}/>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}><IconButton aria-label={descending ? t('items:descending') : t('items:ascending')} onClick={() => setDescending(previous => !previous)}><SwapVertIcon/></IconButton></Stack>

            {isLoading && (
                <ContentSkeleton variant="list"/>
            )}

            {isSuccess && response && (
                <Surface sx={{width: '100%'}}>
                    <Box sx={{p: 2}}>
                        <TextField
                            label={t('common:search')}
                            variant="outlined"
                            focused
                            size="small"
                            fullWidth
                            value={searchPhrase}
                            onChange={(e) => {
                                setSearchPhrase(e.target.value);
                            }}
                            InputProps={{
                                endAdornment: searchPhrase ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchPhrase("")}
                                        >
                                            <Clear fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Box>
                    <PackageTable
                        data={response.data}
                        totalCount={response.totalCount}
                        pageSize={limit}
                        currentPage={page}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        expandedPackageId={expandedPackageId}
                        onTogglePackage={(id) => setExpandedPackageId(previous => previous === id ? null : id)}
                        onAddPurchase={openPurchase}
                        onEdit={openEdit}
                    />
                </Surface>
            )}
            {!isLoading && error && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}


            {selectedPackage && (
                <AddPackagePurchaseDialog pack={selectedPackage} open={purchaseOpen} onClose={() => setPurchaseOpen(false)}/>
            )}
            <EditPackageDialog pack={selectedPackage} open={editOpen} onClose={() => setEditOpen(false)}/>

            {isModalOpen &&
                <CreatePackageDialog
                    open={isModalOpen}
                    closeDialog={handleCloseModal}
                />
            }

        </Box>
    );
};

export default Packagings;
